<template>
  <header
    data-header
    class="fixed z-[1000] flex w-full flex-row items-center overflow-visible border border-r-0 border-l-0 border-t-primary bg-primary"
    id="header-root"
    ref="headerRef">
    <router-link
      id="logo"
      to="/"
      class="flex items-center">
      <span class="flex items-center px-4 py-1.5">
        <img
          :src="logo"
          class="top-1 h-15"
          alt="CAC - Part of The Baldwin Group" />
      </span>
    </router-link>

    <span
      class="flex-1"
      aria-hidden="true" />

    <nav class="flex h-full items-center">
      <NavMenuItem
        title="Clients"
        :links="clientLinks"
        v-if="isLoggedIn && hasRole"
        showBorder />
      <NavMenuItem
        title="Book of Business"
        :links="bookOfBusinessLinks"
        v-if="isLoggedIn && hasRole"
        showBorder />
      <NavMenuItem
        title="Marketplace"
        :links="marketplaceLinks"
        v-if="isLoggedIn && hasRole"
        showBorder />
      <NavMenuItem
        title="Management"
        :links="managementLinks"
        v-if="isLoggedIn && hasRole"
        showBorder />
      <NavMenuItem
        title="Admin"
        :links="adminLinks"
        v-if="isLoggedIn && isAdmin && hasRole"
        showBorder />
    </nav>
    <UserFeedbackModal
      :show="showUserFeedbackModal"
      @close="showUserFeedbackModal = false" />
    <AppButton
      v-if="!isLoggedIn"
      class="border-0 text-white btn-ghost btn-sm hover:bg-white/10 transition-colors"
      @click="signin">
      <CircleUser
        :size="18"
        class="mr-2 stroke-white" />
      Log In
    </AppButton>
    <button
      v-if="canUseUniversalSearch"
      type="button"
      title="Search"
      aria-label="Search"
      data-testid="universal-search-button"
      class="group ml-2 flex h-9 cursor-pointer items-center gap-1.5 rounded-lg border border-white/70 bg-white/10 px-3 transition-colors hover:border-white hover:bg-white/20"
      @click="showUniversalSearch = true">
      <Search
        :size="17"
        class="stroke-white" />
      <span class="text-xs text-white/70">{{ searchShortcutLabel }}</span>
    </button>
    <UniversalSearchModal
      v-if="canUseUniversalSearch"
      v-model:open="showUniversalSearch" />
    <AnnouncementsButton />
    <Notifications v-if="canViewNotifications" />
    <AppMenu
      v-if="isLoggedIn"
      @handle-option="handleMenuOption" />
  </header>
</template>

<script lang="ts">
import Store, { Commit } from '@/store';
import {
  CanViewNotifications,
  GetAllowedAccountIds,
  GetAllowedCarrierIds,
  GetAllowedWholesalerIds,
  IsMarketUser,
  IsUserAClient,
  IsUserAnAdmin,
} from '@/utilities/AuthUtils';
import { Auth } from 'aws-amplify';
import NavMenuItem from '@/components/NavMenuItem.vue';
import UserFeedbackModal from './UserFeedbackModal.vue';
import { StoreMutationType } from '@/types/StoreMutationType';
import logo from '@/assets/images/cac_logo_part_of_TBG_white.svg';
import Notifications from '@/components/Notifications.vue';
import AnnouncementsButton from '@/components/AnnouncementsButton.vue';
import AppMenu from './AppMenu.vue';
import AppButton from '@/components/v2/AppButton.vue';
import UniversalSearchModal from '@/components/UniversalSearchModal.vue';
import { registerResizeItem } from '@/utilities/resize';
import { useHeaderOffset } from '@/composables/useHeaderOffset';
import { nextTick, onMounted, onUnmounted, ref } from 'vue';
import {
  Boxes,
  Building,
  BrickWall,
  ChartColumn,
  ChartColumnStacked,
  ChartNoAxesCombined,
  ChartLine,
  CircleUser,
  ClipboardList,
  Database,
  Factory,
  FileCheck,
  House,
  KeyRound,
  Layers,
  ListChecks,
  NotebookText,
  Loader,
  Palette,
  Percent,
  Search,
  SquarePen,
  Star,
  Table,
  Settings,
} from '@lucide/vue';

export default {
  name: 'AppHeader',
  components: {
    AppButton,
    AnnouncementsButton,
    Notifications,
    NavMenuItem,
    Search,
    UniversalSearchModal,
    UserFeedbackModal,
    AppMenu,
    CircleUser,
  },
  setup() {
    const headerRef = ref<HTMLElement | null>(null);
    const { updateHeaderHeight } = useHeaderOffset();

    const updateHeight = async () => {
      await nextTick();
      const headerElement = document.getElementById('header-root');
      if (headerElement) {
        // offsetHeight, not clientHeight: the header has top and bottom borders, and content
        // offset by clientHeight alone lands 2px under the bottom border.
        const height = headerElement.offsetHeight;
        updateHeaderHeight(height);
      }
    };

    onMounted(() => {
      updateHeight();
      window.addEventListener('resize', updateHeight);
    });

    onUnmounted(() => {
      window.removeEventListener('resize', updateHeight);
      // The offset is a module singleton: without this, a header-less view keeps reserving space
      // for a header that is no longer on the page.
      updateHeaderHeight(0);
    });

    return { headerRef };
  },
  data() {
    return {
      logo: logo,
      showUserFeedbackModal: false,
      showUniversalSearch: false,
    };
  },
  computed: {
    // Gates the modal as well as the button: the modal owns the Cmd/Ctrl+K listener
    canUseUniversalSearch(): boolean {
      return this.isLoggedIn && this.hasRole && !IsUserAClient(Store.state.user);
    },
    searchShortcutLabel(): string {
      return /Mac|iPhone|iPad/.test(navigator.userAgent) ? '⌘K' : 'Ctrl K';
    },
    clientLinks() {
      return [
        {
          title: 'Client Management',
          icon: Building,
          to: IsUserAClient(Store.state.user) ? '/clients' : '/accounts',
          hidden: IsMarketUser(Store.state.user) || (IsUserAClient(Store.state.user) && this.userAllowedAccounts.length <= 1),
        },
        {
          title: 'Analytics Hub',
          to: '/analytics',
          icon: ChartColumn,
          hidden: IsUserAClient(Store.state.user) || IsMarketUser(Store.state.user),
        },
        {
          title: 'Towers',
          to: '/v2/tower',
          icon: ChartColumnStacked,
          hidden: IsUserAClient(Store.state.user) || IsMarketUser(Store.state.user),
        },
        {
          title: 'AI Data Extraction Results',
          to: '/ai-results',
          icon: FileCheck,
          hidden: IsUserAClient(Store.state.user) || IsMarketUser(Store.state.user),
        },
      ];
    },
    bookOfBusinessLinks() {
      return [
        {
          title: 'Home',
          to: '/user/home',
          icon: House,
          badge: 'New',
          hidden: IsUserAClient(Store.state.user) || IsMarketUser(Store.state.user),
        },
        {
          title: 'Policy Explorer',
          icon: Search,
          to: '/policy/explorer',
          hidden: IsUserAClient(Store.state.user) || IsMarketUser(Store.state.user),
        },
      ];
    },
    marketplaceLinks() {
      return [
        {
          title: 'Carrier Analytics',
          icon: Table,
          to: '/carriers',
          hidden: IsUserAClient(Store.state.user) || (IsMarketUser(Store.state.user) && GetAllowedCarrierIds(Store.state.user).length === 0),
        },
        {
          title: 'Wholesaler Analytics',
          icon: BrickWall,
          to: '/wholesaler-group',
          hidden: IsUserAClient(Store.state.user) || (IsMarketUser(Store.state.user) && GetAllowedWholesalerIds(Store.state.user).length === 0),
        },
        {
          title: 'Carrier Survey Results',
          icon: ClipboardList,
          to: '/carriers/surveys',
          hidden: IsUserAClient(Store.state.user) || IsMarketUser(Store.state.user),
        },
        {
          title: 'AM Best Ratings',
          icon: Star,
          to: '/am-best',
          hidden: IsUserAClient(Store.state.user) || IsMarketUser(Store.state.user),
        },
      ];
    },
    managementLinks() {
      return [
        {
          title: 'Management Reporting',
          to: '/management-analytics',
          icon: ChartLine,
          hidden: IsUserAClient(Store.state.user) || IsMarketUser(Store.state.user),
        },
        {
          title: 'Reporting Hub',
          to: '/reporting',
          icon: Factory,
          hidden: IsUserAClient(Store.state.user) || IsMarketUser(Store.state.user),
        },
        {
          title: 'Revenue Dashboard',
          to: '/revenue-dashboard',
          icon: ChartNoAxesCombined,
          hidden: !IsUserAnAdmin(Store.state.user),
        },
        {
          title: 'Yield Analysis',
          to: '/yield-analysis',
          icon: Percent,
          hidden: !IsUserAnAdmin(Store.state.user),
        },
      ];
    },
    adminLinks() {
      const links = [
        { title: 'Carrier Group Mapping', to: '/admin/carrier-issuer', icon: NotebookText },
        { title: 'Policy Type Mapping', to: '/admin/policy-type', icon: SquarePen },
        { title: 'Wholesaler Mapping', to: '/admin/wholesaler', icon: Boxes },
        { title: 'Line Statuses', to: '/admin/line-status', icon: ListChecks },
        { title: 'Sublimit / Subretention Types', to: '/admin/sublimit-types', icon: Layers },
        { title: 'Data Quality', to: '/admin/data-quality', icon: Database },
        { title: 'User Management', to: '/admin/users', icon: KeyRound },
        { title: 'AI Data Extraction', to: '/admin/ai', icon: Loader },
        { title: 'Reporting', to: '/admin/reporting', icon: ChartColumn },
      ];

      // Only add Component Showcase in development
      if (import.meta.env.MODE === 'development') {
        links.push({ title: 'Custom Components', to: '/admin/component-showcase', icon: Palette });
      }

      links.push({ title: 'Advanced Settings', to: '/admin/advanced-settings', icon: Settings });

      return links;
    },
    isLoggedIn: function () {
      return !!Store.state.user;
    },
    isAdmin: function () {
      return IsUserAnAdmin(Store.state.user);
    },
    canViewNotifications: function () {
      return CanViewNotifications(Store.state.user);
    },
    userAllowedAccounts: function () {
      return GetAllowedAccountIds(Store.state.user);
    },
    hasRole: function () {
      // user has at least 1 role
      return (Store.state.user?.roles || []).length > 0;
    },
  },
  methods: {
    async signin() {
      await Auth.federatedSignIn();
    },
    handleMenuOption(option: string) {
      switch (option) {
        case 'feedback':
          this.showUserFeedbackModal = true;
          break;
        case 'client-views':
          Commit(StoreMutationType.SET_SHOW_CLIENT_VIEWS_MODAL, true);
          break;
        case 'notifications':
          this.$router.push('/notifications');
          break;
      }
    },
  },
  mounted() {
    registerResizeItem({ name: 'header', selector: 'header-root' });
  },
};
</script>
